import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { isEmpty } from "../utils/utils";
import Spinner from "./common/Spinner";
import {
  useApi,
  useDateOfBirth,
  useFirstName,
  useLastName,
} from "../store/hooks";
import Toast from "./common/Toast";
import Back from "./common/Back";
import Layout from "./common/Layout";
import { GoToTheNextStep } from "./Step";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

const IdentityInformation = () => {
  const { t, i18n } = useTranslation();
  const [showMandatoryFieldsError, setShowMandatoryFieldsError] =
    useState(false);
  const [invalidDate, setInvalidDate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { patchOnboarding, getWorkflow } = useApi();
  const firstName = useFirstName();
  const lastName = useLastName();
  const dateOfBirth = useDateOfBirth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isEnglish = i18n.language === "en";
  const [formData, setFormData] = useState({
    firstName: firstName,
    lastName: lastName,
    dateOfBirth: dateOfBirth,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (showMandatoryFieldsError) {
      setShowMandatoryFieldsError(false);
    }

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prevState) => ({
        ...prevState,
        [parent]: { ...prevState[parent], [child]: value },
      }));
    } else setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleDateChange = (e) => {
    setInvalidDate(false);
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 8) {
      value = value.slice(0, 8);
    }
    if (value.length >= 8) {
      value =
        value.slice(0, 2) + "/" + value.slice(2, 4) + "/" + value.slice(4, 8);
    } else if (value.length >= 4) {
      value =
        value.slice(0, 2) + "/" + value.slice(2, 4) + "/" + value.slice(4);
    } else if (value.length >= 2) {
      value = value.slice(0, 2) + "/" + value.slice(2);
    }

    setFormData((prevState) => ({ ...prevState, dateOfBirth: value }));
  };

  const validateDate = (dateString) => {
    // Format: MM/DD/YYYY for English, DD/MM/YYYY for others
    const regex = isEnglish
      ? /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/
      : /^(0[1-9]|[12]\d|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
    if (!regex.test(dateString)) return false;

    const parts = dateString.split("/").map(Number);
    const [first, second, year] = parts;
    const month = isEnglish ? first : second;
    const day = isEnglish ? second : first;

    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    if (year < 1900 || year > new Date().getFullYear()) return false;

    const date = new Date(year, month - 1, day);
    const isValidDate =
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day;

    if (!isValidDate) return false;

    const today = new Date();
    today.setHours(23, 59, 59, 999);

    return date < today;
  };

  const transformDateFormat = (dateString) => {
    if (!dateString || dateString.length !== 10) return "";
    const parts = dateString.split("/");
    const [first, second, year] = parts;
    // For English: MM/DD/YYYY -> YYYY-MM-DD
    // For others: DD/MM/YYYY -> YYYY-MM-DD
    const month = isEnglish ? first : second;
    const day = isEnglish ? second : first;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  };

  const validateForm = () => {
    return (
      !isEmpty(formData.dateOfBirth) &&
      !isEmpty(formData.firstName) &&
      !isEmpty(formData.lastName)
    );
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      setIsLoading(true);
      if (!validateDate(formData.dateOfBirth)) {
        setInvalidDate(true);
        setIsLoading(false);
        return;
      }
      if (!validateForm()) {
        setShowMandatoryFieldsError(true);
        setIsLoading(false);
        return;
      }

      const transformedFormData = {
        ...formData,
        dateOfBirth: transformDateFormat(formData.dateOfBirth),
      };
      await patchOnboarding(transformedFormData);
      GoToTheNextStep(navigate, dispatch, getWorkflow);
    } catch (error) {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="az-title">{t("identityInformation.title")}</div>
      <Toast text={t("global.toast.text")} />
      <div className="mt-4">
        <form className="d-flex row justify-content-center">
          <div className="row mt-1">
            <div className="az-formfield">
              <input
                id="firstName"
                name="firstName"
                value={formData.firstName}
                placeholder=" "
                onChange={handleChange}
                className="az-formfield__input"
                disabled={isLoading || !isEmpty(firstName)}
              />
              <label className="az-formfield__label" htmlFor="firstName">
                {t("identityInformation.firstName.label")}
              </label>
            </div>
          </div>

          <div className="row mt-1">
            <div className="az-formfield">
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder=" "
                className="az-formfield__input"
                disabled={isLoading || !isEmpty(lastName)}
              />

              <label htmlFor="lastName" className="az-formfield__label">
                {t("identityInformation.lastName.label")}
              </label>
            </div>
          </div>

          <div className="row mt-1">
            <div className="az-formfield">
              <input
                type="text"
                id="dateOfBirth"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                placeholder=" "
                onChange={handleDateChange}
                className="az-formfield__input"
                disabled={isLoading || !isEmpty(dateOfBirth)}
              />
              <label htmlFor="dateOfBirth" className="az-formfield__label">
                {t("identityInformation.dateOfBirth.label")}
              </label>
            </div>
          </div>

          {showMandatoryFieldsError && (
            <div className="error-message-container mt-4">
              <span className="error-message not-received-span">
                {t("companyIdentification.fields.error")}
              </span>
            </div>
          )}

          {invalidDate && (
            <div className="error-message-container mt-4">
              <span className="error-message not-received-span">
                {t("identityInformation.dateOfBirth.invalid")}
              </span>
            </div>
          )}

          <div className="mt-5 d-flex justify-content-start">
            {isLoading ? (
              <div className="w-100 az-btn disabled">
                <Spinner size={24} />
              </div>
            ) : (
              <button
                onClick={handleSubmit}
                className="w-100 az-btn text-uppercase"
              >
                {t("identityInformation.confirm.btn.label")}
              </button>
            )}
          </div>
        </form>
      </div>
      <Back message={t("back.label")} />
    </Layout>
  );
};

export default IdentityInformation;
